import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-toggle-content.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import '@chancenlogistik/client/components/logistik-component-base-header';
import '@chancenlogistik/client/components/logistik-component-base-page-layout';
import '@chancenlogistik/client/components/logistik-component-cart-list';
import '@chancenlogistik/client/components/logistik-component-edit-chancen-brief-variables';
import '@chancenlogistik/client/components/logistik-component-product-availability';
import { g_logistikCart } from '@chancenlogistik/client/globalSubjects.js';
import { LogistikRoutes } from '@chancenlogistik/client/routes.js';
import '@chancenwerk/client/design/components/cr-loader.js';
import { LogistikCart, LogistikCartItem } from '@chancenwerk/db/LogistikCart.js';
import { LogistikSettings } from '@chancenwerk/db/LogistikClearanceSettings.js';
import { LogistikProduct, LogistikProductSet, WorkshopPack } from '@chancenwerk/db/LogistikProduct.js';
import '@chancenwerk/design/components/cr-image.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { from, of, switchMap } from 'rxjs';
import { ChromiumDialog } from '../../../client/design/components/cr-dialog.js';
import { isEntireCartValid, reconstructCartItem } from '../helper.js';
import {
  calculateAllowedCartChange,
  type CartChangeRequest,
  type ChangeResult,
  type ErrorResult,
} from '../helpers/order-amount-range.js';

const productSelectionSet = {
  ...selectionSet(() => LogistikProduct, 1),
  selectedProducts: { _id: 1, quantity: 1, productID: 1 },
  flyaVariables: { variableName: 1 },
};

/**
 * @customElement logistik-page-cart
 */
@customElement('logistik-page-cart')
export class LogistikPageCart extends ChemistryLitElement {
  @state() protected _maxOrderQuantity = new RXController(this, Promise.resolve(0));

  @state() protected readonly _productsInCart = new RXController(this, g_logistikCart, cart$ =>
    cart$.pipe(
      switchMap(cart =>
        cart.items
          ? from(
              getByID<typeof LogistikProduct>(
                LogistikProduct,
                cart.items.map(i => i.productID),
              )(productSelectionSet),
            )
          : of([] as NarrowedEntity<LogistikProduct, typeof productSelectionSet>[]),
      ),
    ),
  );
  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());
  @state() private readonly _setting = new RXController(this, LogistikSettings.get()(LogistikSettings.allFields));

  @state() protected readonly _totalProductCount = new RXController(this, this._productsInCart.observable, obs =>
    obs.pipe(
      switchMap(products => {
        return of(
          g_logistikCart.value.items.reduce(
            (acc, item) => acc + item.quantity * this.getCartItemMultiplier(item, products),
            0,
          ),
        );
      }),
    ),
  );

  override render() {
    if (this._currentUser.isLoading || this._setting.isLoading || this._productsInCart.isLoading)
      return html` <x-flex flex center crossaxis-center> <cr-loader></cr-loader> </x-flex> `;

    return html`
      <logistik-component-base-page-layout>
        <logistik-component-base-header
          pageTitle="Warenkorb"
          backRoute=${LogistikRoutes.landing}
        ></logistik-component-base-header>
        <cr-flex space-between space="lg" ${css({ height: '100%' })}>
          <cr-button
            ?disabled=${!this._productsInCart.value.length}
            mode="outline"
            icon="delete"
            @click=${async () => {
              const confirm = await ChromiumDialog.confirm('Möchten Sie den Warenkorb wirklich leeren?');
              if (!confirm) return;
              g_logistikCart.next(new LogistikCart({}));
            }}
            ${css({ marginLeft: 'auto' })}
          >
            Warenkorb leeren
          </cr-button>
          <logistik-component-cart-list
            .cartItems=${g_logistikCart.value.items}
            @delete-non-available=${() => {
              g_logistikCart.value.items = g_logistikCart.value.items.filter(item =>
                this._productsInCart.value?.find(i => i._id === item.productID),
              );
              g_logistikCart.next(g_logistikCart.value);
              this.requestUpdate();
            }}
            @item-removed=${(e: CustomEvent<{ productID: string; index: number }>) => {
              if (g_logistikCart.value.items[e.detail.index]?.productID !== e.detail.productID) {
                logger.error(
                  {
                    index: e.detail.index,
                    productID: e.detail.productID,
                    cartItems: g_logistikCart.value.items.map(x => x.productID),
                  },
                  'index doesnt match with product id when removing item from cart',
                );
                return ChromiumDialog.alert(
                  'Es gab ein Problem beim Entfernen des Produkts, bitte wenden Sie sich an die CHANCENlogistik',
                );
              }
              g_logistikCart.value.items.splice(e.detail.index, 1);
              // g_logistikCart.value.items = g_logistikCart.value.items.filter(i => i.productID !== e.detail.productID);
              g_logistikCart.next(g_logistikCart.value);
              this.dispatchEvent(new CustomEvent('item-deleted'));
            }}
            @amount-changed=${(e: CustomEvent<{ amount: number; productID: string; index: number }>) =>
              this.onAmountChanged(e)}
          ></logistik-component-cart-list>
          ${this.renderContinueButton()}
        </cr-flex>
      </logistik-component-base-page-layout>
    `;
  }

  protected renderAdditionalOrderInformation(): Renderable {
    const total = this._totalProductCount.value;
    if (!total) return nothing;
    const isPlural = total > 1;
    return html`
      <cr-flex padding="sm">
        <cr-text ${css({ alignSelf: 'flex-end' })}>
          <b>${total} ${isPlural ? 'Produkte im Warenkorb' : 'Produkt im Warenkorb'}</b>
        </cr-text>
      </cr-flex>
    `;
  }

  private renderContinueButton() {
    if (g_logistikCart.value.items !== null && g_logistikCart.value.items.length > 0) {
      return html`
        <cr-flex>
          ${this.renderAdditionalOrderInformation()}
          <cr-button
            @click=${() => {
              if (!isEntireCartValid(g_logistikCart.value, this._productsInCart.value)) {
                return ChromiumDialog.alert('Bitte füllen Sie alle notwendigen Felder aus.');
              }
              return this.onContinueClicked();
            }}
            icon="arrow_forward"
            ${css({ alignSelf: 'flex-end' })}
          >
            Weiter
          </cr-button>
        </cr-flex>
      `;
    } else {
      return nothing;
    }
  }

  protected onContinueClicked() {
    goTo(LogistikRoutes.checkout);
  }

  protected getTotalOrderQuantity() {
    // this variant disregards that LogistikProductSets have multiple items within them
    return g_logistikCart.value.items.reduce((acc, item) => acc + item.quantity, 0);
  }

  protected getCartItemMultiplier(
    item: LogistikCartItem,
    products: NarrowedEntity<LogistikProduct, { _class: 1; _id: 1; productIDs: 1 }>[],
  ): number {
    const product = products.find(p => p._id === item.productID);
    if (!product || product._class !== LogistikProductSet._class) return 1;
    return (product as LogistikProductSet).selectedProducts.length;
  }

  protected getAllowedCartChange(changeRequest: CartChangeRequest) {
    return calculateAllowedCartChange(changeRequest);
  }

  protected async onAmountChanged(e: CustomEvent<{ amount: number; productID: string; index: number }>) {
    if (g_logistikCart.value.items[e.detail.index]?.productID !== e.detail.productID) {
      logger.error(
        {
          index: e.detail.index,
          productID: e.detail.productID,
          cartItems: g_logistikCart.value.items.map(x => x.productID),
        },
        'index doesnt match with product id when removing item from cart',
      );
      return ChromiumDialog.alert(
        'Es gab ein Problem beim Entfernen des Produkts, bitte wenden Sie sich an die CHANCENlogistik',
      );
    }

    const cartItem = g_logistikCart.value.items[e.detail.index];
    if (!cartItem) return;

    const product = this._productsInCart.value.find(prod => prod._id === e.detail.productID);
    if (!product) {
      logger.error(
        {
          index: e.detail.index,
          productID: e.detail.productID,
          cartItems: g_logistikCart.value.items.map(v => v.productID),
        },
        'Request product does not exist in cart',
      );
      return ChromiumDialog.alert(
        'Es gab ein Problem beim Ändern des Warenkorbs, bitte wenden Sie sich an die CHANCENlogistik',
      );
    }

    const allowedChange = this.getAllowedCartChange({
      type: 'change',
      cart: g_logistikCart.value,
      newQuantity: e.detail.amount,
      product,
      productsInCart: this._productsInCart.value,
      cartItem,
      tokenMaxOrderQuantity: this._maxOrderQuantity.value,
    });

    switch (allowedChange.type) {
      case 'error':
        this._handleErrorResult(e, allowedChange);
        break;
      case 'add':
        this._handleAddResult(e);
        break;
      case 'change':
        await this._handleChangeResult(e, allowedChange, product, cartItem);
        break;
      case 'none':
        void ChromiumDialog.alert('Diese Änderung des Warenkorbs ist nicht erlaubt');
        break;
      case 'redundant':
        break;
    }
  }

  private _handleErrorResult(
    e: CustomEvent<{ amount: number; productID: string; index: number }>,
    allowedChange: ErrorResult,
  ) {
    logger.error(
      {
        index: e.detail.index,
        productID: e.detail.productID,
        cartItems: g_logistikCart.value.items.map(v => v.productID),
      },
      allowedChange.reason,
    );
    void ChromiumDialog.alert(
      'Es gab ein Problem beim Ändern des Warenkorbs, bitte wenden Sie sich an die CHANCENlogistik',
    );
  }

  private _handleAddResult(e: CustomEvent<{ amount: number; productID: string; index: number }>) {
    logger.error(
      {
        index: e.detail.index,
        productID: e.detail.productID,
        cartItems: g_logistikCart.value.items.map(v => v.productID),
      },
      "allowedChange.type should be 'change' but is 'add'",
    );
    void ChromiumDialog.alert(
      'Es gab ein Problem beim Ändern des Warenkorbs, bitte wenden Sie sich an die CHANCENlogistik',
    );
  }

  private async _handleChangeResult(
    e: CustomEvent<{ amount: number; productID: string; index: number }>,
    allowedChange: ChangeResult,
    product: NarrowedEntity<LogistikProduct, typeof productSelectionSet>,
    cartItem: LogistikCartItem,
  ) {
    if (!allowedChange.accepted) {
      const isLowerThanRequested = allowedChange.lower;
      const isWorkshop = product instanceof WorkshopPack;
      const message = ((): string => {
        if (isWorkshop && !isLowerThanRequested) return `Minimale Anzahl der Teilnehmenden wurde nicht eingehalten`;
        else if (isWorkshop && isLowerThanRequested) return `Maximale Anzahl der Teilnehmenden wurde nicht eingehalten`;
        else if (!isWorkshop && !isLowerThanRequested) return `Minimale Bestellmenge wurde nicht eingehalten`;
        // / !isWorkshop && isLowerThanRequested
        else return `Maximale Bestellmenge wurde nicht eingehalten`;
      })();
      await ChromiumDialog.alert(message);
      this.requestUpdate();
      return;
    }

    cartItem.quantity = e.detail.amount;
    g_logistikCart.value.items = g_logistikCart.value.items.map(i =>
      i._id === cartItem._id ? reconstructCartItem(i, e.detail.amount - i.quantity) : i,
    );
    g_logistikCart.next(g_logistikCart.value);
    return;
  }
}
